import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, MapPin, Search, Star, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { api, fmtUSD } from '@/lib/api';
import { JobeurCard } from '@/components/JobeurCard';
import { CategoryTile } from '@/components/CategoryTile';
import { EmptyState } from '@/components/Primitives';

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(params.get('tab') || 'services');
  const [filterOpen, setFilterOpen] = useState(false);

  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const city = params.get('city') || '';
  const commune = params.get('commune') || '';
  const minRating = params.get('min_rating') || '';

  useEffect(() => {
    Promise.all([api.get('/catalog/categories'), api.get('/catalog/cities')]).then(([c, ci]) => {
      setCategories(c.data.items || []);
      setCities(ci.data.items || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (category) p.category = category;
    if (city) p.city = city;
    if (commune) p.commune = commune;
    if (minRating) p.min_rating = minRating;
    if (tab === 'services') {
      api.get('/services', { params: p }).then((r) => setServices(r.data.items || [])).finally(() => setLoading(false));
    } else {
      api.get('/requests', { params: p }).then((r) => setRequests(r.data.items || [])).finally(() => setLoading(false));
    }
  }, [q, category, city, commune, minRating, tab]);

  const setP = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };

  const chips = [
    category && { label: categories.find((c) => c.slug === category)?.label || category, clear: () => setP('category', '') },
    city && { label: city, clear: () => setP('city', '') },
    commune && { label: commune, clear: () => setP('commune', '') },
    minRating && { label: `${minRating}★ et +`, clear: () => setP('min_rating', '') },
    q && { label: `“${q}”`, clear: () => setP('q', '') },
  ].filter(Boolean);

  const selectedCity = cities.find((c) => c.name === city);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-[var(--radius)] px-3 h-12">
          <Search size={18} className="text-slate-500" />
          <input
            type="text"
            placeholder="Rechercher un service, un métier…"
            defaultValue={q}
            onKeyDown={(e) => e.key === 'Enter' && setP('q', e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="search-input"
          />
        </div>
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-12" data-testid="listing-open-filters-button">
              <Filter size={16} /> <span className="hidden sm:inline ml-1">Filtres</span>
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[360px]">
            <SheetHeader>
              <SheetTitle>Filtres</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Catégorie</Label>
                <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={category} onChange={(e) => setP('category', e.target.value)} data-testid="filter-category">
                  <option value="">Toutes</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Ville</Label>
                <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={city} onChange={(e) => { setP('city', e.target.value); setP('commune', ''); }} data-testid="filter-city">
                  <option value="">Toutes</option>
                  {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              {selectedCity && (
                <div>
                  <Label>Commune</Label>
                  <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={commune} onChange={(e) => setP('commune', e.target.value)} data-testid="filter-commune">
                    <option value="">Toutes</option>
                    {selectedCity.communes.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label>Note minimum</Label>
                <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={minRating} onChange={(e) => setP('min_rating', e.target.value)} data-testid="filter-rating">
                  <option value="">Toutes</option>
                  <option value="3">3★ et +</option>
                  <option value="4">4★ et +</option>
                  <option value="4.5">4.5★ et +</option>
                </select>
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button onClick={() => setFilterOpen(false)} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="filters-apply-button">Appliquer</Button>
              <Button variant="outline" onClick={() => setParams({})} className="w-full" data-testid="filters-reset-button">Réinitialiser</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {chips.map((c, i) => (
            <button key={i} onClick={c.clear} className="inline-flex items-center gap-1 text-xs bg-[#EAF3FF] text-[#0E3F80] px-2.5 py-1 rounded-full border border-[#C7E2FF]">
              {c.label} <X size={12} />
            </button>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="services" data-testid="search-tab-services">Services proposés</TabsTrigger>
          <TabsTrigger value="requests" data-testid="search-tab-requests">Demandes ouvertes</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-32 rounded-xl shimmer" />)}
            </div>
          ) : services.length === 0 ? (
            <EmptyState icon={Search} title="Aucun service trouvé" description="Essayez une autre ville ou élargissez la catégorie." testid="empty-services" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {services.map((s) => (
                <Link key={s.id} to={`/service/${s.id}`} className="block" data-testid={`service-card-${s.id}`}>
                  <Card className="p-4 hover:border-[#C7E2FF] hover:shadow-md">
                    <h3 className="font-semibold text-[#0B1220]">{s.title}</h3>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{s.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm font-mono-num font-semibold text-[#1677F2]">{fmtUSD(s.price_cents)} {s.price_type === 'hourly' ? '/h' : ''}</p>
                      <p className="text-xs text-slate-600 flex items-center gap-1"><MapPin size={11} /> {s.jobeur_commune || s.jobeur_city}</p>
                    </div>
                    <div className="mt-3 border-t border-[#E2E8F0] pt-3">
                      <JobeurCard jobeur={{
                        id: s.jobeur_id,
                        first_name: s.jobeur_name?.split(' ')[0] || '',
                        last_name: s.jobeur_name?.split(' ').slice(1).join(' ') || '',
                        photo_base64: s.jobeur_photo,
                        city: s.jobeur_city,
                        commune: s.jobeur_commune,
                        rating_avg: s.jobeur_rating_avg,
                        rating_count: s.jobeur_rating_count,
                        phone_verified: s.jobeur_phone_verified,
                        id_verified: s.jobeur_id_verified,
                      }} service={null} />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="requests" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-xl shimmer" />)}
            </div>
          ) : requests.length === 0 ? (
            <EmptyState icon={Search} title="Aucune demande ouverte" description="Essayez d&apos;autres critères." testid="empty-requests" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {requests.map((r) => (
                <Link key={r.id} to={`/request/${r.id}`}>
                  <Card className="p-4 hover:border-[#C7E2FF] hover:shadow-md" data-testid={`request-card-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#0B1220] truncate">{r.title}</h3>
                      {r.urgent && <span className="text-[10px] font-bold bg-[#CE1021] text-white px-2 py-0.5 rounded-full uppercase">Urgent</span>}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {r.commune}, {r.city}</p>
                    <p className="text-sm text-slate-700 mt-2 line-clamp-2">{r.description}</p>
                    {r.budget_cents > 0 && <p className="text-xs mt-2 font-mono-num">Budget: {fmtUSD(r.budget_cents)}</p>}
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
